import {
  MODALITIES,
  VOICE_STATUS_MESSAGES,
  ballAnnouncement,
  modalityDefinition,
  modalityName,
  roundStartAnnouncement,
  type CardCell,
  type EventSettings,
  type EventState,
  type Modality,
  type RoundState,
  type ServerMessage,
} from '@bingo/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Icon, IconSprite } from '../../components/icons.js';
import { MiniCardStatic } from '../../components/MiniCard.js';
import { Stage } from '../../components/Stage.js';
import { ApiError, api, uploadPhoto } from '../../lib/api.js';
import { sendCommand } from '../../lib/commands.js';
import {
  advanceMark,
  markForRound,
  shouldNarrateBall,
  type NarrationMark,
} from '../../lib/ballNarration.js';
import { currentRound, reduceEventState } from '../../lib/eventState.js';
import { vibrate } from '../../lib/haptics.js';
import { setGameLive } from '../../lib/liveGame.js';
import { useWakeLock } from '../../lib/wakeLock.js';
import { accessToken, sessionSnapshot } from '../../lib/session.js';
import { shouldSpeak } from '../../lib/narration.js';
import { cancelSpeech, dropQueuedSpeech, speak } from '../../lib/speech.js';
import { useRevealedRound } from '../../lib/useRevealedRound.js';
import {
  currentNativeVoice,
  refreshVoices,
  unlockFromGesture,
  useVoiceState,
} from '../../lib/voiceStore.js';
import { connectRealtime, type ConnectionStatus } from '../../lib/ws.js';
import { ClaimDialog } from './ClaimDialog.js';
import { NarrationPanel } from './NarrationPanel.js';
import { WinnerDialog } from './WinnerDialog.js';

/**
 * O /controle — o que o operador usa, de pé, com o celular ou tablet na mão.
 *
 * Nenhum comando sai daqui por WebSocket: tudo é POST com `Idempotency-Key`,
 * para que o segundo toque num botão (rede ruim, dedo nervoso) devolva a mesma
 * resposta em vez de queimar outra bola. O estado vem do WebSocket, igual ao
 * do telão — é isso que garante que as duas telas nunca divirjam.
 */

type Dialog =
  | { kind: 'none' }
  | { kind: 'claim'; claimId: string; cardNumber: string | null }
  | { kind: 'winner'; claimId: string; cardNumber: string | null }
  | { kind: 'round'; isNew: boolean }
  | { kind: 'narration' };

export function Controle() {
  const [params] = useSearchParams();
  const eventId = params.get('eventId');

  const [state, setState] = useState<EventState | null>(null);
  const [status, setStatus] = useState<ConnectionStatus>('connecting');
  const [dialog, setDialog] = useState<Dialog>({ kind: 'none' });
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const voice = useVoiceState();

  const stateRef = useRef<EventState | null>(null);
  stateRef.current = state;

  const round = currentRound(state);
  const game = state?.game ?? null;
  const settings = state?.settings;

  /**
   * O controle vê a MESMA bola que o telão, no mesmo instante: enquanto há
   * bola em suspense ela não aparece em lugar nenhum e o operador lê
   * "Sorteando…". Sem isso, ele saberia o número antes do público — e cantaria
   * antes do telão.
   */
  const revealed = useRevealedRound(round);
  const visibleRound = useMemo(
    () => (round ? { ...round, balls: revealed.visibleBalls } : null),
    [round, revealed.visibleBalls],
  );
  const sorteando = Boolean(revealed.pending);

  /**
   * Partida aberta = o celular não pode apagar a tela nem ser atualizado por
   * conta própria. O /controle fica minutos sem toque entre um sorteio e
   * outro, tempo de sobra para o aparelho bloquear com o salão esperando.
   */
  const gameOpen = Boolean(game && !game.endedAt);
  useWakeLock(gameOpen);

  useEffect(() => {
    setGameLive(gameOpen);
    return () => setGameLive(false);
  }, [gameOpen]);

  /**
   * Sem WebSocket, o /controle não sabe o estado do jogo — e mandar comando
   * às cegas é como sortear sem ver o painel. Os botões ficam desabilitados
   * e a faixa diz o que está acontecendo, em vez de deixar o operador
   * tocando num botão que não responde.
   */
  const offline = status !== 'open';

  /** Comando só sai com conexão: offline é tão bloqueante quanto ocupado. */
  const bloqueado = busy || offline;

  /**
   * Com fonte Telão quem fala é a TV — e **nunca os dois ao mesmo tempo**,
   * que no salão sairia como eco. A regra é a de `lib/narration.ts`, a mesma
   * que o telão aplica.
   */
  const device = { kind: 'PHONE' as const, unlocked: voice.unlocked };
  const canSpeakHere = settings ? shouldSpeak(device, settings, { force: true }) : false;
  const narrationHere = settings ? shouldSpeak(device, settings) : false;

  const flash = useCallback((text: string) => {
    setToast(text);
    window.setTimeout(() => setToast(null), 2600);
  }, []);

  const handleMessage = useCallback(
    (incoming: ServerMessage) => {
      setState((previous) => reduceEventState(previous, incoming));

      // "Repetir": com fonte Celular, quem fala é este aparelho. Chega pelo
      // MESMO evento que o telão recebe, então os dois reagem juntos — e só
      // um deles fala.
      if (incoming.type === 'ball:repeat' && canSpeakHere) {
        speak(ballAnnouncement(incoming.data.number, { digits: settings?.digits ?? true }), {
          voice: currentNativeVoice(),
          rate: settings?.rate ?? 0.95,
          kind: 'ball',
        });
      }

      if (incoming.type === 'round:started') {
        // Reinicia o marcador pela rodada que ACABOU de começar — painel
        // zerado vira `ordinal: 0`. Sem isto, o ordinal reiniciava em 1 e
        // ficava abaixo do marcador da rodada anterior: o celular passava a
        // rodada inteira sem cantar bola nenhuma.
        narrationMark.current = markForRound(incoming.data.round);
        // Mesma razão do telão: a bola em espera era do painel anterior.
        dropQueuedSpeech();

        // Com fonte Celular o anúncio da rodada também sai daqui: senão a
        // TV ficaria muda (ela cedeu a vez) e o celular cantaria só números,
        // sem dizer o que está valendo.
        if (narrationHere) {
          speak(
            roundStartAnnouncement({
              roundNumber: incoming.data.round.number,
              modality: incoming.data.round.modality,
              keptBalls: incoming.data.keptBalls,
            }),
            {
              voice: currentNativeVoice(),
              rate: settings?.rate ?? 0.95,
              kind: 'announcement',
            },
          );
        }
      }

      if (incoming.type === 'claim:result') {
        // Padrões diferentes porque é a informação que o operador precisa
        // passar adiante na hora — e ele a recebe sem olhar para a tela.
        if (incoming.data.status === 'NAO') {
          flash('Bingo não confirmado · o jogo continua');
          vibrate('bingoRejected', settings?.vibration ?? true);
        } else if (incoming.data.status === 'SIM' || incoming.data.status === 'MANUAL') {
          vibrate('bingoConfirmed', settings?.vibration ?? true);
        }
      }
    },
    [flash, canSpeakHere, narrationHere, settings?.digits, settings?.rate, settings?.vibration],
  );

  /** Mesmo motivo do telão: a conexão não pode depender do handler. */
  const handleMessageRef = useRef(handleMessage);
  handleMessageRef.current = handleMessage;

  useEffect(() => {
    if (!eventId) return;
    let connection: ReturnType<typeof connectRealtime> | null = null;
    let cancelled = false;

    void accessToken().then((token) => {
      if (!token || cancelled) return;

      connection = connectRealtime({
        query: { eventId, at: token },
        handlers: {
          onMessage: (incoming) => handleMessageRef.current(incoming),
          onStatus: setStatus,
        },
      });
    });

    return () => {
      cancelled = true;
      connection?.close();
    };
  }, [eventId]);

  /**
   * Narração pelo celular: dispara em `revealAt`, no mesmo instante do telão.
   *
   * Narrar ao ENVIAR o comando faria o celular cantar o número 1,3 segundo
   * antes de ele aparecer na TV — exatamente o problema que o `revealAt`
   * existe para resolver.
   */
  const narrationMark = useRef<NarrationMark | null>(null);

  useEffect(() => {
    const last = revealed.lastRevealed;
    if (!last || !round) return;

    const nova = shouldNarrateBall(narrationMark.current, round.id, last);
    narrationMark.current = advanceMark(round.id, last);

    if (!nova) return;

    // Vibra mesmo com a narração desligada: o operador fica com o olho no
    // salão, não na tela, e o toque é o que confirma "a bola saiu".
    vibrate('ball', settings?.vibration ?? true);

    if (!narrationHere || !voice.unlocked) return;

    speak(ballAnnouncement(last.number, { digits: settings?.digits ?? true }), {
      voice: currentNativeVoice(),
      rate: settings?.rate ?? 0.95,
      kind: 'ball',
    });
  }, [
    round?.id,
    revealed.lastRevealed?.ordinal,
    narrationHere,
    voice.unlocked,
    settings?.digits,
    settings?.rate,
    settings?.vibration,
  ]);

  /** Carrega as vozes do próprio celular, para a lista de ajustes. */
  useEffect(() => {
    void refreshVoices(settings?.voice || null);
  }, [settings?.voice]);

  useEffect(() => {
    if (!narrationHere) cancelSpeech();
  }, [narrationHere]);

  /**
   * Destrava o áudio DESTE aparelho no próprio toque do operador.
   *
   * O navegador só libera voz dentro de um gesto, e o gesto tem que ser
   * síncrono — por isso isto roda no início do handler, antes de qualquer
   * `await`. Vale para quem reabre o /controle com a fonte Celular já
   * escolhida: o toque em Sortear ou Repetir é o gesto.
   */
  const armPhoneAudio = useCallback(() => {
    if (settings?.voiceSource === 'PHONE' && !voice.unlocked) unlockFromGesture();
  }, [settings?.voiceSource, voice.unlocked]);

  /** Executa um comando, cuidando de ocupado/erro de forma uniforme. */
  const run = useCallback(
    async <T,>(action: () => Promise<T>): Promise<T | null> => {
      setBusy(true);
      try {
        return await action();
      } catch (err) {
        flash(err instanceof ApiError ? err.message : 'Não foi possível completar a ação.');
        return null;
      } finally {
        setBusy(false);
      }
    },
    [flash],
  );

  const draw = useCallback(
    (forced?: number) => {
      if (!round) return;
      armPhoneAudio();

      void run(() =>
        sendCommand(`/api/rounds/${round.id}/draws`, {
          body: {
            source: forced ? 'GLOBE' : state?.settings.drawMode === 'AUTO' ? 'AUTO' : 'MANUAL',
            ...(forced ? { number: forced } : {}),
          },
        }),
      );
    },
    [round, run, state?.settings.drawMode, armPhoneAudio],
  );

  const undo = useCallback(() => {
    if (!round) return;

    void run(() => sendCommand(`/api/rounds/${round.id}/draws/last`, { method: 'DELETE' }));
  }, [round, run]);

  /**
   * Repetir é um COMANDO, não um efeito local.
   *
   * Antes o número só aparecia aqui e nada mais acontecia: o telão ficava
   * parado e ninguém ouvia nada. Agora o servidor publica `ball:repeat` para a
   * sala, e quem reage é o aparelho certo.
   */
  const repeat = useCallback(() => {
    if (!round) return;
    armPhoneAudio();

    void run(() => sendCommand(`/api/rounds/${round.id}/repeat`));
  }, [round, run, armPhoneAudio]);

  const toggleAuto = useCallback(() => {
    if (!eventId || !state) return;

    void run(() =>
      api(`/api/events/${eventId}/auto`, {
        method: 'POST',
        body: { running: !state.autoDraw.running, intervalSec: state.settings.autoIntervalSec },
      }),
    );
  }, [eventId, run, state]);

  const openClaim = useCallback(() => {
    if (!round) return;

    void run(async () => {
      const result = await sendCommand<{ claimId: string; cardNumber: string | null }>(
        `/api/rounds/${round.id}/claims`,
        { body: {} },
      );

      setDialog({ kind: 'claim', claimId: result.claimId, cardNumber: result.cardNumber });
      return result;
    });
  }, [round, run]);

  const resolveClaim = useCallback(
    (input: {
      cells: CardCell[];
      decision: 'CONFIRM' | 'FORCE' | 'REJECT' | 'CANCEL';
      cardNumber: string | null;
    }) => {
      if (dialog.kind !== 'claim') return;
      const { claimId } = dialog;

      void run(async () => {
        const result = await sendCommand<{ status: string }>(`/api/claims/${claimId}/resolve`, {
          body: input,
        });

        if (result.status === 'SIM' || result.status === 'MANUAL') {
          setDialog({ kind: 'winner', claimId, cardNumber: input.cardNumber });
        } else {
          setDialog({ kind: 'none' });
        }

        return result;
      });
    },
    [dialog, run],
  );

  const confirmWinner = useCallback(
    (input: { name: string; cardNumber: string | null; photo: Blob | null }) => {
      if (dialog.kind !== 'winner' || !eventId) return;
      const { claimId } = dialog;

      void run(async () => {
        const photoKey = input.photo ? await uploadPhoto(eventId, input.photo) : null;

        const result = await sendCommand('/api/winners', {
          body: { claimId, name: input.name, cardNumber: input.cardNumber, photoKey },
        });

        setDialog({ kind: 'none' });
        return result;
      });
    },
    [dialog, eventId, run],
  );

  const startRound = useCallback(
    (input: { modality: Modality; prizeText: string; keepBalls: boolean; isNewGame: boolean }) => {
      if (!eventId) return;

      void run(async () => {
        const result = input.isNewGame
          ? await sendCommand(`/api/events/${eventId}/games`, {
              body: {
                modality: input.modality,
                prizeText: input.prizeText || null,
                freeCenter: state?.settings.freeCenter ?? true,
              },
            })
          : await sendCommand(`/api/games/${game!.id}/rounds`, {
              body: {
                modality: input.modality,
                prizeText: input.prizeText || null,
                freeCenter: state?.settings.freeCenter ?? true,
                keepBalls: input.keepBalls,
              },
            });

        setDialog({ kind: 'none' });
        return result;
      });
    },
    [eventId, game, round?.number, run, state?.settings.freeCenter],
  );

  /** Salva os ajustes de narração; o telão recebe pelo WebSocket. */
  const updateSettings = useCallback(
    (patch: Partial<EventSettings>) => {
      if (!eventId) return;

      void run(() => api(`/api/events/${eventId}`, { method: 'PATCH', body: { settings: patch } }));
    },
    [eventId, run],
  );

  const testVoiceOnDisplay = useCallback(
    (voiceURI: string | null) => {
      if (!eventId) return;

      void run(() =>
        api(`/api/events/${eventId}/voice-test`, { method: 'POST', body: { voiceURI } }),
      );
    },
    [eventId, run],
  );

  const finishGame = useCallback(() => {
    if (!game) return;

    void run(() => sendCommand(`/api/games/${game.id}/finish`));
  }, [game, run]);

  // Atalhos de teclado do protótipo (tablet com teclado acoplado).
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && /INPUT|TEXTAREA|SELECT/.test(target.tagName)) return;
      if (dialog.kind !== 'none' || event.ctrlKey || event.metaKey || event.altKey) return;

      const key = event.key.toLowerCase();

      if (key === ' ' || key === 'enter') {
        if (target?.tagName === 'BUTTON') return;
        event.preventDefault();
        if (state?.settings.drawMode !== 'GLOBE') draw();
      } else if (key === 'a') toggleAuto();
      else if (key === 'z') undo();
      else if (key === 'b') openClaim();
      else if (key === 'r') repeat();
      else if (key === 'n') setDialog({ kind: 'narration' });
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dialog.kind, draw, openClaim, repeat, state, toggleAuto, undo]);

  if (!eventId) {
    return (
      <main className="mobile">
        <h1>Controle</h1>
        <p className="hint-text">Abra pelo QR da TV ou escolha um evento no /admin.</p>
      </main>
    );
  }

  if (!state) {
    return (
      <main className="mobile">
        <h1>Carregando…</h1>
        <p className="hint-text">
          {status === 'open' ? 'Sincronizando' : 'Conectando ao servidor'}
        </p>
      </main>
    );
  }

  const problemDisplay = state.displays.find((display) => display.voiceStatus !== 'READY');
  const globe = state.settings.drawMode === 'GLOBE';

  return (
    <div className="controle">
      <IconSprite />

      <Stage
        state={state}
        round={visibleRound}
        bigBall={revealed.lastRevealed?.number ?? null}
        animation={null}
        /* Durante o giro o operador vê o mesmo que o público: nada. */
        suspenseBall={null}
        {...(globe && round && !sorteando ? { onPick: (ball: number) => draw(ball) } : {})}
        autoBar={
          state.autoDraw.running ? (
            <div className="autobar">
              <i style={{ width: '100%' }} />
            </div>
          ) : null
        }
        controls={
          <div className="controls">
            <button
              className="btn primary"
              disabled={bloqueado || globe || sorteando || !round || round.balls.length >= 75}
              onClick={() => draw()}
            >
              <Icon name="i-play" />
              {sorteando ? 'Sorteando…' : globe ? 'Clique no número' : 'Sortear'}
            </button>

            <button
              className={`btn${state.autoDraw.running ? ' on' : ''}`}
              disabled={bloqueado || !round}
              onClick={toggleAuto}
            >
              <Icon name={state.autoDraw.running ? 'i-pause' : 'i-play'} />
              {state.autoDraw.running ? 'Pausar' : 'Auto'}
            </button>

            <button
              className="btn bingo"
              disabled={bloqueado || !round?.balls.length}
              onClick={openClaim}
            >
              BINGO!
            </button>

            {/* Durante o giro, repetir por cima confundiria o público. */}
            <button
              className="btn"
              disabled={bloqueado || sorteando || !revealed.lastRevealed}
              onClick={repeat}
              title={sorteando ? 'Aguarde a bola ser revelada' : undefined}
            >
              <Icon name="i-repeat" />
              Repetir
            </button>

            <button
              className="btn"
              disabled={bloqueado || !round || round.balls.length <= round.carriedCount}
              onClick={undo}
            >
              <Icon name="i-undo" />
              Desfazer
            </button>

            <button
              className={`btn${settings?.narration ? ' on' : ''}`}
              onClick={() => setDialog({ kind: 'narration' })}
            >
              <Icon name="i-mic" />
              Narração
            </button>

            <button
              className="btn"
              disabled={busy}
              onClick={() => setDialog({ kind: 'round', isNew: true })}
            >
              <Icon name="i-new" />
              Nova rodada
            </button>

            <button
              className="btn"
              disabled={bloqueado || !game || Boolean(game.endedAt)}
              onClick={finishGame}
            >
              <Icon name="i-flag" />
              Finalizar partida
            </button>
          </div>
        }
      />

      {offline ? (
        <div className="offline-banner" role="status" aria-live="polite">
          <span className="offline-dot" aria-hidden="true" />
          <div>
            <strong>Sem conexão com o servidor</strong>
            <small>Reconectando… Os comandos voltam sozinhos assim que a conexão voltar.</small>
          </div>
        </div>
      ) : null}

      <div className="controle-bar">
        <span className={`pill${status === 'open' ? ' ok' : ''}`}>
          {status === 'open' ? 'conectado' : 'reconectando…'}
        </span>

        {/* Estado do áudio do telão em texto acionável: "mudo" não diz a
            quem opera o que fazer; estas mensagens dizem. */}
        {state.displays.length === 0 ? (
          <span className="pill warn">nenhum telão conectado</span>
        ) : problemDisplay ? (
          <button
            type="button"
            className="pill warn"
            onClick={() => setDialog({ kind: 'narration' })}
          >
            {VOICE_STATUS_MESSAGES[problemDisplay.voiceStatus]}
          </button>
        ) : (
          <span className="pill ok">telão com voz pronta</span>
        )}

        {sorteando ? <span className="pill">sorteando…</span> : null}

        <span className="pill">{sessionSnapshot().user?.name ?? ''}</span>
      </div>

      {dialog.kind === 'claim' && round ? (
        <ClaimDialog
          drawn={round.balls.map((ball) => ball.number)}
          modality={round.modality}
          freeCenter={round.freeCenter}
          initialCardNumber={dialog.cardNumber}
          onResolve={resolveClaim}
          busy={busy}
        />
      ) : null}

      {dialog.kind === 'winner' ? (
        <WinnerDialog
          cardNumber={dialog.cardNumber}
          busy={busy}
          onCancel={() => setDialog({ kind: 'none' })}
          onConfirm={confirmWinner}
        />
      ) : null}

      {dialog.kind === 'narration' && settings ? (
        <NarrationPanel
          settings={settings}
          displays={state.displays}
          busy={busy}
          onClose={() => setDialog({ kind: 'none' })}
          onChange={updateSettings}
          onTestOnDisplay={testVoiceOnDisplay}
        />
      ) : null}

      {dialog.kind === 'round' ? (
        <RoundDialog
          current={round}
          hasOpenGame={Boolean(game && !game.endedAt)}
          busy={busy}
          onCancel={() => setDialog({ kind: 'none' })}
          onStart={startRound}
        />
      ) : null}

      {toast ? <div className="toast">{toast}</div> : null}
    </div>
  );
}

/** Escolha da próxima rodada — modalidade, prêmio e o destino das bolas. */
function RoundDialog({
  current,
  hasOpenGame,
  busy,
  onCancel,
  onStart,
}: {
  current: RoundState | null;
  hasOpenGame: boolean;
  busy: boolean;
  onCancel: () => void;
  onStart: (input: {
    modality: Modality;
    prizeText: string;
    keepBalls: boolean;
    isNewGame: boolean;
  }) => void;
}) {
  const [modality, setModality] = useState<Modality>(current?.modality ?? 'FULL_CARD');
  const [prizeText, setPrizeText] = useState('');
  const [keepBalls, setKeepBalls] = useState(true);
  const [newGame, setNewGame] = useState(!hasOpenGame);

  const canKeep = hasOpenGame && (current?.balls.length ?? 0) > 0 && !newGame;

  return (
    <div className="overlay">
      <form
        className="modal"
        onSubmit={(event) => {
          event.preventDefault();
          onStart({ modality, prizeText, keepBalls: canKeep && keepBalls, isNewGame: newGame });
        }}
      >
        <h2>{newGame ? 'Nova partida' : 'Nova rodada'}</h2>
        <p className="sub">
          {newGame
            ? 'Uma partida nova zera o painel e o relógio geral.'
            : 'Escolha o que vale nesta rodada.'}
        </p>

        <div className="modes">
          {MODALITIES.map((key) => (
            <label key={key}>
              <input
                type="radio"
                name="modality"
                value={key}
                checked={modality === key}
                onChange={() => setModality(key)}
              />
              <span className="mc">
                <MiniCardStatic pattern={modalityDefinition(key).icon} />
                {modalityName(key)}
              </span>
            </label>
          ))}
        </div>

        {canKeep ? (
          <fieldset className="fs">
            <legend>Bolas já sorteadas</legend>
            <div className="seg seg2">
              <label>
                <input
                  type="radio"
                  name="keep"
                  checked={keepBalls}
                  onChange={() => setKeepBalls(true)}
                />
                <span>
                  Continuar com as bolas
                  <small>as {current?.balls.length} bolas continuam valendo · mesmo lote</small>
                </span>
              </label>
              <label>
                <input
                  type="radio"
                  name="keep"
                  checked={!keepBalls}
                  onChange={() => setKeepBalls(false)}
                />
                <span>
                  Zerar o painel<small>novo lote de cartelas</small>
                </span>
              </label>
            </div>
          </fieldset>
        ) : null}

        {hasOpenGame ? (
          <label className="check">
            <input
              type="checkbox"
              checked={newGame}
              onChange={(event) => setNewGame(event.target.checked)}
            />
            Começar uma partida nova (zera tudo)
          </label>
        ) : null}

        <label>
          Prêmio desta rodada
          <input
            value={prizeText}
            onChange={(event) => setPrizeText(event.target.value)}
            placeholder="Ex.: Cesta de Natal, R$ 500"
          />
        </label>

        <div className="actions">
          <button type="button" className="btn" onClick={onCancel}>
            Cancelar
          </button>
          <button type="submit" className="btn primary" disabled={busy}>
            Começar rodada
          </button>
        </div>
      </form>
    </div>
  );
}
